import { normalizeHexColor } from '../../utils/colorUtils';

/**
 * Pulls a business's logo, brand colors, and name off their public website.
 *
 * Routemize needs three things to brand a booking page: a logo big enough to
 * print at the top of the form, one or two brand colors, and the business
 * name. This module gets them from a URL the user types during onboarding.
 *
 * The browser can't fetch a third-party site directly, so the site's HTML has
 * to come through a public CORS proxy. Proxies rate-limit, die, and sometimes
 * answer with their own landing page instead of the site — and a huge share of
 * small trade businesses run Wix/Squarespace/GoDaddy sites that render their
 * header in JavaScript, so even a successful fetch often contains no logo at
 * all. Treating "fetch the HTML" as the one path to an answer is why this kept
 * coming back empty.
 *
 * So there are two independent tracks, run at the same time and merged:
 *
 *   1. HTML — best-effort enrichment. Gives the real logo, the CSS palette,
 *      og:site_name, and the description when it works.
 *   2. Domain icons — derived from the hostname alone, so it works on a
 *      JS-rendered site and when every proxy is down.
 *
 * Track 2 always produces something, which means the user always lands on a
 * usable result and the HTML track is free to fail quietly.
 */

export type BrandLogoType = 'logo' | 'icon' | 'og-image' | 'favicon';

export interface BrandLogoCandidate {
  url: string;
  type: BrandLogoType;
  label: string;
  /** Higher means more likely to be the real brand logo. */
  score: number;
  /** Intrinsic size once probed. 0 means unknown (or an SVG with no intrinsic size). */
  width: number;
  height: number;
}

export interface NormalizedSite {
  href: string;
  origin: string;
  hostname: string;
}

export interface BrandFetchResult {
  logo: string;
  logos: BrandLogoCandidate[];
  favicon: string;
  colors: string[];
  cssColors: string[];
  cssColorFrequencies: Record<string, number>;
  title: string;
  businessName: string;
  description: string;
  /** True when the site's HTML never arrived and only domain-derived icons are present. */
  isFallback: boolean;
}

export interface ImageProbe {
  ok: boolean;
  width: number;
  height: number;
}

export interface BrandFetchDeps {
  fetchHtml: (site: NormalizedSite) => Promise<string | null>;
  probeImage: (url: string) => Promise<ImageProbe>;
}

/**
 * Resolve with the first attempt that succeeds, ignoring rejections until all
 * of them have failed. `Promise.race` settles on the first attempt to *settle*,
 * success or failure, which would hand us whichever proxy errored quickest.
 *
 * Hand-rolled because `Promise.any` is ES2021 and this project targets ES2020.
 */
export function raceToFirstSuccess<T>(factories: Array<() => Promise<T>>): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    if (factories.length === 0) {
      reject(new Error('No candidates to race'));
      return;
    }
    let remaining = factories.length;
    let settled = false;
    let lastError: unknown = null;
    factories.forEach((factory) => {
      factory()
        .then((value) => {
          if (!settled) {
            settled = true;
            resolve(value);
          }
        })
        .catch((err) => {
          lastError = err;
          remaining -= 1;
          if (remaining === 0 && !settled) {
            reject(lastError instanceof Error ? lastError : new Error('All candidates failed'));
          }
        });
    });
  });
}

/**
 * Accepts what people actually type: "acme.com", "www.acme.com/about",
 * "HTTPS://Acme.com ". Returns null for anything that isn't a real http(s)
 * host, so a typo fails fast instead of sending junk to four proxies.
 */
export function normalizeSiteUrl(raw: string): NormalizedSite | null {
  const trimmed = (raw || '').trim();
  if (!trimmed) return null;

  const withProtocol = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;

  try {
    const url = new URL(withProtocol);
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return null;
    // A bare word like "painting" parses as a valid URL but has no TLD, and a
    // hostname with no dot can never resolve publicly.
    if (!url.hostname.includes('.')) return null;
    if (url.hostname.startsWith('.') || url.hostname.endsWith('.')) return null;
    return { href: url.href, origin: url.origin, hostname: url.hostname };
  } catch {
    return null;
  }
}

/** "www.acme-painting.com" -> "acme-painting" */
export function bareDomainLabel(hostname: string): string {
  return hostname.replace(/^www\./i, '').split('.')[0] || '';
}

/**
 * Titles that mean the proxy answered for itself rather than for the site.
 *
 * This is the specific failure that made the old version untrustworthy:
 * corsproxy.io started returning its own marketing page, which is valid HTML
 * hundreds of characters long, so the "did we get HTML?" length check passed
 * and we happily scraped *the proxy's* logo and colors. Because it answered
 * faster than the real proxies it usually won the race, so users saw someone
 * else's branding presented as their own.
 */
const IMPOSTOR_TITLE_MARKERS = [
  'corsproxy',
  'allorigins',
  'codetabs',
  'cors proxy',
  'just a moment',
  'attention required',
  'access denied',
  'checking your browser',
  'enable javascript and cookies',
  'are you a robot',
  'error 1',
  '403 forbidden',
  '404 not found',
  'site not found',
  'domain for sale',
  'buy this domain',
  'this site can’t be reached',
  "this site can't be reached",
];

/**
 * Guards against a proxy handing back its own page, a Cloudflare challenge, or
 * a parked-domain placeholder. Everything downstream trusts the HTML, so this
 * is the gate that keeps another company's branding out of the results.
 */
export function looksLikeSiteHtml(html: string | null | undefined): boolean {
  if (!html) return false;
  const text = html.trim();
  if (text.length < 200) return false;

  const lower = text.toLowerCase();
  if (!lower.includes('<html') && !lower.includes('<head') && !lower.includes('<body')) {
    return false;
  }

  const titleMatch = lower.match(/<title[^>]*>([\s\S]{0,200}?)<\/title>/);
  const title = titleMatch ? titleMatch[1].trim() : '';
  if (title && IMPOSTOR_TITLE_MARKERS.some((marker) => title.includes(marker))) {
    return false;
  }

  return true;
}

const NEUTRAL_RGB_SPREAD = 30;

/**
 * Greys, near-blacks and near-whites are structural (text, borders,
 * backgrounds) on nearly every site, so they'd dominate a frequency count
 * without saying anything about the brand.
 */
export function isNeutralHex(hex: string): boolean {
  const normalized = normalizeHexColor(hex);
  if (!normalized) return true;
  const value = normalized.replace('#', '');
  const r = parseInt(value.slice(0, 2), 16);
  const g = parseInt(value.slice(2, 4), 16);
  const b = parseInt(value.slice(4, 6), 16);
  const spread = Math.max(r, g, b) - Math.min(r, g, b);
  return spread < NEUTRAL_RGB_SPREAD;
}

const HEX_PATTERN = /#([0-9a-f]{6}|[0-9a-f]{3})\b/gi;
const RGB_PATTERN = /rgba?\(\s*(\d{1,3})\s*[, ]\s*(\d{1,3})\s*[, ]\s*(\d{1,3})/gi;
/** `--brand-primary: #ff6600` — an explicitly named brand token. */
const CSS_VARIABLE_PATTERN = /--[\w-]*\s*:\s*(#[0-9a-f]{3,6}\b|rgba?\([^)]*\))/gi;

const rgbToHex = (r: number, g: number, b: number): string | null => {
  if ([r, g, b].some((c) => Number.isNaN(c) || c < 0 || c > 255)) return null;
  return `#${[r, g, b].map((c) => c.toString(16).padStart(2, '0')).join('')}`;
};

/**
 * Weighted frequency map of the brand colors a site declares.
 *
 * Weights encode how deliberate each source is. `theme-color` and CSS custom
 * properties are named brand tokens, so one appearance outweighs dozens of
 * incidental hexes buried in a stylesheet.
 */
export function collectCssColors(doc: Document): Record<string, number> {
  const frequencies: Record<string, number> = {};

  const add = (raw: string, weight: number) => {
    const normalized = normalizeHexColor(raw);
    if (!normalized || isNeutralHex(normalized)) return;
    frequencies[normalized] = (frequencies[normalized] || 0) + weight;
  };

  const addFromText = (text: string, weight: number) => {
    for (const match of text.matchAll(HEX_PATTERN)) add(match[0], weight);
    for (const match of text.matchAll(RGB_PATTERN)) {
      const hex = rgbToHex(Number(match[1]), Number(match[2]), Number(match[3]));
      if (hex) add(hex, weight);
    }
  };

  const themeColor = doc.querySelector('meta[name="theme-color"]')?.getAttribute('content');
  if (themeColor) add(themeColor, 100);

  doc.querySelectorAll('style').forEach((style) => {
    const css = style.textContent || '';
    for (const match of css.matchAll(CSS_VARIABLE_PATTERN)) {
      const value = match[1];
      if (value.startsWith('#')) {
        add(value, 25);
      } else {
        const rgb = value.match(/(\d{1,3})\s*[, ]\s*(\d{1,3})\s*[, ]\s*(\d{1,3})/);
        if (rgb) {
          const hex = rgbToHex(Number(rgb[1]), Number(rgb[2]), Number(rgb[3]));
          if (hex) add(hex, 25);
        }
      }
    }
    addFromText(css, 1);
  });

  doc.querySelectorAll('[style]').forEach((el) => {
    addFromText(el.getAttribute('style') || '', 1);
  });

  return frequencies;
}

/** Hexes ordered strongest brand signal first. */
export function orderedCssColors(frequencies: Record<string, number>, limit = 6): string[] {
  return Object.entries(frequencies)
    .sort((a, b) => b[1] - a[1])
    .slice(0, limit)
    .map(([hex]) => hex);
}

const SKIP_URL_MARKERS = ['tracking', 'pixel', 'spacer', '1x1', 'blank.gif', 'transparent.gif'];

const absoluteUrl = (raw: string, base: string): string | null => {
  const value = (raw || '').trim();
  if (!value || value.length < 5) return null;
  if (value.startsWith('data:')) return null;
  const lower = value.toLowerCase();
  if (SKIP_URL_MARKERS.some((marker) => lower.includes(marker))) return null;
  try {
    return new URL(value.startsWith('//') ? `https:${value}` : value, base).href;
  } catch {
    return null;
  }
};

/**
 * Pick the largest entry from a `srcset`, since the author already told us
 * which rendition is highest resolution.
 */
const largestFromSrcset = (srcset: string): string | null => {
  const entries = srcset
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean)
    .map((part) => {
      const [url, descriptor] = part.split(/\s+/);
      const width = descriptor?.endsWith('w') ? parseInt(descriptor, 10) : 0;
      return { url, width: Number.isNaN(width) ? 0 : width };
    })
    .filter((entry) => Boolean(entry.url));
  if (entries.length === 0) return null;
  return entries.sort((a, b) => b.width - a.width)[0].url;
};

const BASE_TYPE_SCORE: Record<BrandLogoType, number> = {
  logo: 100,
  'og-image': 55,
  icon: 45,
  favicon: 25,
};

/**
 * Everything the HTML can tell us. Parsed with `DOMParser` rather than the
 * regex battery this replaced: the old patterns needed a separate expression
 * for every attribute order (`class` before `src` and `src` before `class`,
 * and again for `id` and `alt`), and still missed lazy-loaded images and
 * `srcset`. A real parse handles all of that and can also ask where an element
 * sits in the document, which is the strongest logo signal there is.
 */
export function parseBrandFromHtml(html: string, site: NormalizedSite) {
  const doc = new DOMParser().parseFromString(html, 'text/html');

  const meta = (selector: string): string =>
    doc.querySelector(selector)?.getAttribute('content')?.trim() || '';

  const title = doc.querySelector('title')?.textContent?.trim() || '';
  const description = meta('meta[name="description"]') || meta('meta[property="og:description"]');
  const siteName = meta('meta[property="og:site_name"]');
  const ogImage = meta('meta[property="og:image"]') || meta('meta[name="twitter:image"]');

  const candidates: Array<Omit<BrandLogoCandidate, 'width' | 'height'>> = [];
  const seen = new Set<string>();

  const push = (rawUrl: string | null, type: BrandLogoType, label: string, bonus = 0) => {
    if (!rawUrl) return;
    const url = absoluteUrl(rawUrl, site.href);
    if (!url || seen.has(url)) return;
    seen.add(url);
    candidates.push({ url, type, label, score: BASE_TYPE_SCORE[type] + bonus });
  };

  doc.querySelectorAll('img').forEach((img) => {
    const src =
      img.getAttribute('src') ||
      img.getAttribute('data-src') ||
      img.getAttribute('data-lazy-src') ||
      (img.getAttribute('srcset') ? largestFromSrcset(img.getAttribute('srcset') || '') : null);
    if (!src) return;

    const haystack = [
      img.getAttribute('class'),
      img.getAttribute('id'),
      img.getAttribute('alt'),
      src,
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();

    const named = /\b(logo|brand|wordmark)\b/.test(haystack) || haystack.includes('logo');
    const inHeader = Boolean(img.closest('header, nav, .header, .navbar, [class*="header"]'));
    const inFooter = Boolean(img.closest('footer, .footer, [class*="footer"]'));

    if (!named && !inHeader) return;

    let bonus = 0;
    if (named) bonus += 30;
    if (inHeader) bonus += 20;
    // A footer logo is usually a smaller monochrome variant of the real mark.
    if (inFooter) bonus -= 15;

    const label = named ? 'Main Logo' : 'Header Image';
    push(src, 'logo', label, bonus);
  });

  // Declared icons, largest first — `sizes="180x180"` is the author telling us
  // the resolution, which beats guessing.
  const iconLinks = Array.from(
    doc.querySelectorAll('link[rel~="icon"], link[rel="shortcut icon"], link[rel="apple-touch-icon"]')
  )
    .map((link) => {
      const sizes = link.getAttribute('sizes') || '';
      const declared = parseInt(sizes.split('x')[0], 10);
      return {
        href: link.getAttribute('href'),
        rel: (link.getAttribute('rel') || '').toLowerCase(),
        size: Number.isNaN(declared) ? 0 : declared,
      };
    })
    .filter((entry) => Boolean(entry.href))
    .sort((a, b) => b.size - a.size);

  iconLinks.forEach((entry) => {
    const isApple = entry.rel.includes('apple');
    push(
      entry.href,
      isApple ? 'icon' : 'favicon',
      isApple ? 'Apple Touch Icon' : 'Favicon',
      Math.min(entry.size, 256) / 16
    );
  });

  push(ogImage, 'og-image', 'Social Preview Image');

  const cssColorFrequencies = collectCssColors(doc);

  return {
    title,
    description,
    siteName,
    candidates,
    cssColorFrequencies,
  };
}

/**
 * Logo sources that need only the hostname.
 *
 * This is what makes the tool work on the JS-rendered site builders most
 * trade businesses use, where the HTML contains no logo, and when every proxy
 * is rate-limited. unavatar and icon.horse both resolve a domain to the best
 * mark they can find (apple-touch icon, manifest icon, social avatar) and
 * serve it CORS-open at a usable size.
 */
export function iconServiceCandidates(
  site: NormalizedSite
): Array<Omit<BrandLogoCandidate, 'width' | 'height'>> {
  const { hostname, origin } = site;
  return [
    {
      url: `https://unavatar.io/${hostname}?fallback=false`,
      type: 'icon',
      label: 'Brand Icon',
      score: BASE_TYPE_SCORE.icon + 20,
    },
    {
      url: `https://icon.horse/icon/${hostname}`,
      type: 'icon',
      label: 'Site Icon',
      score: BASE_TYPE_SCORE.icon + 10,
    },
    {
      url: `${origin}/apple-touch-icon.png`,
      type: 'icon',
      label: 'Apple Touch Icon',
      score: BASE_TYPE_SCORE.icon,
    },
    {
      // 256px rather than the 128px this used to request: at 128 the result was
      // too soft to use as a booking-page logo, so it only ever looked like a
      // failure to the user.
      url: `https://www.google.com/s2/favicons?domain=${hostname}&sz=256`,
      type: 'favicon',
      label: 'Site Icon (via Google)',
      score: BASE_TYPE_SCORE.favicon,
    },
    {
      url: `${origin}/favicon.ico`,
      type: 'favicon',
      label: 'Favicon',
      score: BASE_TYPE_SCORE.favicon - 5,
    },
  ];
}

const MIN_USABLE_EDGE = 32;

/**
 * Fold measured size into the ranking and drop anything that didn't load.
 *
 * Probing is what stops dead URLs and 16px favicons from being offered as a
 * logo. SVGs commonly report no intrinsic size, so an unmeasured-but-loaded
 * image keeps its score rather than being penalised.
 */
export function scoreProbedCandidate(
  candidate: Omit<BrandLogoCandidate, 'width' | 'height'>,
  probe: ImageProbe
): BrandLogoCandidate | null {
  if (!probe.ok) return null;

  const edge = Math.max(probe.width, probe.height);
  let score = candidate.score;

  if (edge > 0) {
    if (edge < MIN_USABLE_EDGE) return null;
    // Diminishing bonus so a 2000px hero doesn't automatically beat a 300px
    // logo that carried real naming signals.
    score += Math.min(40, Math.round(Math.log2(edge / MIN_USABLE_EDGE) * 12));

    const ratio = probe.width && probe.height ? probe.width / probe.height : 1;
    // Logos are square-ish or wide wordmarks; anything taller than it is wide,
    // or extremely wide, is usually a banner or a photo.
    if (ratio > 0.2 && ratio < 8) score += 10;
  }

  return { ...candidate, score, width: probe.width, height: probe.height };
}

export function rankCandidates(candidates: BrandLogoCandidate[]): BrandLogoCandidate[] {
  const byUrl = new Map<string, BrandLogoCandidate>();
  candidates.forEach((candidate) => {
    const existing = byUrl.get(candidate.url);
    if (!existing || candidate.score > existing.score) byUrl.set(candidate.url, candidate);
  });
  return Array.from(byUrl.values()).sort((a, b) => b.score - a.score);
}

const GENERIC_TITLE_WORDS = ['home', 'welcome', 'official', 'website', 'site', 'page', 'main', 'index'];

const SERVICE_DESCRIPTION_MARKERS = [
  'services in',
  'serving',
  'residential',
  'commercial',
  'professional',
  'affordable',
  'quality',
  'licensed',
  'insured',
  'free estimates',
  'free quote',
  'near me',
  'in your area',
  'your local',
  'trusted',
  'we offer',
  'we provide',
  'specializing in',
];

/**
 * Page titles are usually "Acme Painting | Interior & Exterior Painting in
 * Denver". The second half is marketing copy, not the business name.
 */
export function looksLikeServiceDescription(value: string): boolean {
  const lower = value.toLowerCase();
  if (lower.length > 60) return true;
  if (/\b(in|near|serving|around)\s+[A-Z]/.test(value)) return true;
  return SERVICE_DESCRIPTION_MARKERS.filter((marker) => lower.includes(marker)).length >= 2;
}

/** Trade words that mark where a run-together domain splits. */
const BUSINESS_SUFFIXES = [
  'painting', 'plumbing', 'electric', 'electrical', 'roofing', 'landscaping',
  'cleaning', 'construction', 'moving', 'dental', 'auto', 'hvac', 'salon',
  'fitness', 'photo', 'media', 'design', 'tech', 'services', 'solutions',
  'group', 'pro', 'pros',
];

const titleCase = (value: string): string =>
  value
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');

/** "acmepainting" -> "Acme Painting", "acme-painting" -> "Acme Painting". */
export function businessNameFromDomain(hostname: string): string {
  const label = bareDomainLabel(hostname);
  if (!label) return '';
  if (/[-_]/.test(label)) return titleCase(label.replace(/[-_]+/g, ' '));

  const lower = label.toLowerCase();
  for (const suffix of BUSINESS_SUFFIXES) {
    if (lower.length > suffix.length && lower.endsWith(suffix)) {
      return titleCase(`${lower.slice(0, -suffix.length)} ${suffix}`);
    }
  }
  return titleCase(lower);
}

export function deriveBusinessName(
  parsed: { siteName: string; title: string } | null,
  hostname: string
): string {
  if (parsed?.siteName) return parsed.siteName;

  if (parsed?.title) {
    const candidates = parsed.title
      .split(/\s*[|\-–—:]\s*/)
      .map((part) => part.replace(/^(welcome to|official site of|official website of)\s*/i, '').trim())
      .filter(Boolean)
      .filter((part) => !GENERIC_TITLE_WORDS.includes(part.toLowerCase()))
      .filter((part) => !looksLikeServiceDescription(part));

    if (candidates.length > 0) {
      // Business names are shorter than the taglines they sit beside.
      return candidates.reduce((shortest, current) =>
        current.length < shortest.length ? current : shortest
      );
    }
  }

  return businessNameFromDomain(hostname);
}

const HTML_FETCH_TIMEOUT_MS = 7000;
const IMAGE_PROBE_TIMEOUT_MS = 6000;

/**
 * corsproxy.io is deliberately absent — see IMPOSTOR_TITLE_MARKERS. Even with
 * validation in place it costs a request slot to return a page we always throw
 * away.
 */
const htmlProxyAttempts = (href: string): Array<() => Promise<string>> => {
  const readRaw = (proxyUrl: string) => async (): Promise<string> => {
    const response = await fetch(proxyUrl, {
      headers: { Accept: 'text/html' },
      signal: AbortSignal.timeout(HTML_FETCH_TIMEOUT_MS),
    });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const text = await response.text();
    if (!looksLikeSiteHtml(text)) throw new Error('Response was not the requested site');
    return text;
  };

  const encoded = encodeURIComponent(href);

  return [
    readRaw(`https://api.allorigins.win/raw?url=${encoded}`),
    readRaw(`https://api.codetabs.com/v1/proxy?quest=${encoded}`),
    async () => {
      const response = await fetch(`https://api.allorigins.win/get?url=${encoded}`, {
        signal: AbortSignal.timeout(HTML_FETCH_TIMEOUT_MS),
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      const contents = typeof data?.contents === 'string' ? data.contents : '';
      if (!looksLikeSiteHtml(contents)) throw new Error('Response was not the requested site');
      return contents;
    },
  ];
};

const defaultFetchHtml = async (site: NormalizedSite): Promise<string | null> => {
  try {
    return await raceToFirstSuccess(htmlProxyAttempts(site.href));
  } catch {
    return null;
  }
};

const defaultProbeImage = (url: string): Promise<ImageProbe> =>
  new Promise((resolve) => {
    if (typeof window === 'undefined' || typeof window.Image !== 'function') {
      resolve({ ok: false, width: 0, height: 0 });
      return;
    }

    const img = new window.Image();
    let settled = false;

    const finish = (probe: ImageProbe) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      img.onload = null;
      img.onerror = null;
      resolve(probe);
    };

    const timer = setTimeout(() => finish({ ok: false, width: 0, height: 0 }), IMAGE_PROBE_TIMEOUT_MS);

    img.onload = () => finish({ ok: true, width: img.naturalWidth, height: img.naturalHeight });
    img.onerror = () => finish({ ok: false, width: 0, height: 0 });
    img.src = url;
  });

/**
 * Run both tracks, keep whatever each one returns, and never throw.
 *
 * Callers get a usable result even when the HTML never arrives; `isFallback`
 * says whether the site itself was reached so the UI can set expectations.
 */
export async function fetchBrandAssets(
  rawUrl: string,
  deps: Partial<BrandFetchDeps> = {}
): Promise<BrandFetchResult | null> {
  const site = normalizeSiteUrl(rawUrl);
  if (!site) return null;

  const fetchHtml = deps.fetchHtml ?? defaultFetchHtml;
  const probeImage = deps.probeImage ?? defaultProbeImage;

  const html = await fetchHtml(site).catch(() => null);
  const parsed = html && looksLikeSiteHtml(html) ? parseBrandFromHtml(html, site) : null;

  const unverified = [...(parsed?.candidates ?? []), ...iconServiceCandidates(site)];

  const probed = await Promise.all(
    unverified.map(async (candidate) => {
      const probe = await probeImage(candidate.url).catch(() => ({
        ok: false,
        width: 0,
        height: 0,
      }));
      return scoreProbedCandidate(candidate, probe);
    })
  );

  const logos = rankCandidates(probed.filter((entry): entry is BrandLogoCandidate => entry !== null));

  const cssColorFrequencies = parsed?.cssColorFrequencies ?? {};
  const cssColors = orderedCssColors(cssColorFrequencies);

  return {
    logo: logos[0]?.url ?? '',
    logos,
    favicon: logos.find((entry) => entry.type === 'favicon' || entry.type === 'icon')?.url ?? '',
    colors: cssColors,
    cssColors,
    cssColorFrequencies,
    title: parsed?.title ?? '',
    businessName: deriveBusinessName(parsed, site.hostname),
    description: parsed?.description ?? '',
    isFallback: parsed === null,
  };
}
